import { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { useSiteData } from '../context/SiteDataContext';
import Reveal from '../components/Reveal';
import BeforeAfter from '../components/BeforeAfter';
import type { GalleryItem } from '../lib/types';

export default function Gallery() {
  const { gallery } = useSiteData();
  const [filter, setFilter] = useState<string>('all');
  const [lightbox, setLightbox] = useState<GalleryItem | null>(null);

  const categories = useMemo(
    () => ['all', ...Array.from(new Set(gallery.map((g) => g.category)))],
    [gallery],
  );
  const shown = filter === 'all' ? gallery : gallery.filter((g) => g.category === filter);

  return (
    <div>
      <section className="relative border-b border-white/[0.06]">
        <div className="hero-grid absolute inset-0" />
        <div className="container-x relative py-16 text-center sm:py-20">
          <Reveal>
            <span className="eyebrow">Portfolio</span>
            <h1 className="heading-xl mt-4">The Work Speaks</h1>
            <p className="mx-auto mt-5 max-w-xl text-lg text-zinc-400">
              Real vehicles, real results. Every shot below left our shop looking like this.
            </p>
          </Reveal>
          <Reveal delay={100}>
            <div className="mt-8 flex flex-wrap justify-center gap-2">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setFilter(c)}
                  className={`rounded-full px-4 py-2 text-sm font-semibold capitalize transition-all ${
                    filter === c
                      ? 'bg-gradient-to-r from-accent-600 to-sky-500 text-white'
                      : 'glass text-zinc-400 hover:text-white'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="container-x py-14">
        {shown.length === 0 ? (
          <p className="py-20 text-center text-zinc-500">No photos in this category yet — check back soon.</p>
        ) : (
          <div className="columns-1 gap-4 sm:columns-2 lg:columns-3 [&>*]:mb-4">
            {shown.map((g, i) => (
              <Reveal key={g.id} delay={(i % 3) * 80}>
                {g.beforeUrl ? (
                  <div className="overflow-hidden rounded-2xl border border-white/10">
                    <BeforeAfter beforeUrl={g.beforeUrl} afterUrl={g.url} alt={g.caption} />
                    <div className="bg-night-900/80 px-4 py-3">
                      <span className="text-sm font-semibold text-white">{g.caption}</span>
                      <span className="ml-2 text-xs capitalize text-accent-300">{g.category}</span>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setLightbox(g)}
                    className="group relative block w-full overflow-hidden rounded-2xl border border-white/10 text-left"
                  >
                    <img
                      src={g.url}
                      alt={g.caption}
                      loading="lazy"
                      className="w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-night-950/85 via-transparent to-transparent p-4 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                      <span className="text-sm font-semibold text-white">{g.caption}</span>
                      <span className="text-xs capitalize text-accent-300">{g.category}</span>
                    </div>
                  </button>
                )}
              </Reveal>
            ))}
          </div>
        )}
      </section>

      {lightbox && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-night-950/95 p-4 backdrop-blur-sm"
          onClick={() => setLightbox(null)}
        >
          <button
            className="absolute right-5 top-5 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
            aria-label="Close"
          >
            <X size={22} />
          </button>
          <figure className="max-h-[90vh] max-w-5xl" onClick={(e) => e.stopPropagation()}>
            <img src={lightbox.url} alt={lightbox.caption} className="max-h-[80vh] w-full rounded-2xl object-contain" />
            <figcaption className="mt-3 text-center">
              <span className="font-medium text-white">{lightbox.caption}</span>
              <span className="ml-2 text-sm capitalize text-accent-300">{lightbox.category}</span>
            </figcaption>
          </figure>
        </div>
      )}
    </div>
  );
}
